// Реквізити контрагента для первинних документів — з усіх джерел, які вже є в eCabinet, з походженням кожного поля.
// Пріоритет: основний рахунок у «Контрагентах» (IBAN, банк) → введене вами раніше → реєстри ДПС (обліку, ПДВ — під сесією компанії) → ЄДР (відкриті дані НАІС) → ЄРПН.
// Банківських реквізитів і email у реєстрах немає — лише з рахунків у «Контрагентах» і ваших документів. Нічого не вигадуємо: немає джерела — порожньо.

// «ІВАНЕНКО ІВАН ІВАНОВИЧ» → «Іваненко Іван Іванович»; змішаний регістр лишаємо як є
export function properCase(s) {
  const t = String(s || "").trim();
  if (!t || t !== t.toUpperCase()) return t;
  return t.toLowerCase().replace(/(^|[\s\-'’ʼ(])(\p{L})/gu, (m, p, ch) => p + ch.toUpperCase());
}

// Підписант із ЄДР: «ІВАНЕНКО ІВАН; Директор - керівник» або «Шуть Катерина Вікторівна - керівник».
// Повертаємо першого з роллю «керівник» (інакше — першого); посада — якщо вказана, інакше «Керівник»
export function edrSigner(signers = []) {
  const parsed = (signers || []).map((s) => {
    const [who, rest = ""] = String(s).split(/;\s*/);
    const [nameMaybe, roleTail] = who.split(/\s+-\s+/);
    const pos = rest ? rest.split(/\s+-\s+/)[0].trim() : "";
    const role = (rest.split(/\s+-\s+/)[1] || roleTail || "").trim().toLowerCase();
    return { name: properCase(nameMaybe.trim()), position: pos ? pos.charAt(0).toUpperCase() + pos.slice(1) : "", role };
  }).filter((x) => x.name);
  const head = parsed.find((x) => /керівник/.test(x.role)) || parsed[0];
  return head ? { name: head.name, position: head.position || "Керівник" } : null;
}

// account — основний гривневий рахунок з «Контрагентів» { iban, bank }; prev — counterparty з попередніх документів; dps — { verdict (interpretRegisters), checkedAt }; edr — рядок ЄДР; erpn — { name, ipn }
export function mergeCounterparty(tin, { account = null, prev = null, dps = null, edr = null, erpn = null, edrAsOf = null } = {}) {
  const fields = { tin, name: "", ipn: "", address: "", iban: "", bank: "", email: "", vatStatus: "", signer: { position: "", name: "", authority: "" } };
  const sources = {}, warnings = [];
  const put = (key, value, source) => {
    const v = typeof value === "string" ? value.trim() : value;
    if (!v) return;
    if (key.startsWith("signer.")) { const k = key.slice(7); if (fields.signer[k]) return; fields.signer[k] = v; }
    else { if (fields[key]) return; fields[key] = v; }
    sources[key] = source;
  };
  const v = dps?.verdict;
  const dpsLabel = dps?.checkedAt ? `реєстри ДПС, ${String(dps.checkedAt).slice(0, 10).split("-").reverse().join(".")}` : "реєстри ДПС";
  // 0. Рахунок, який ви зберегли в картці контрагента
  if (account) { put("iban", account.iban, "рахунки контрагента"); put("bank", account.bank, "рахунки контрагента"); }
  // 1. Ваші попередні документи
  if (prev) {
    for (const k of ["name", "ipn", "address", "iban", "bank", "email"]) put(k, prev[k], "ваші попередні документи");
    for (const k of ["position", "name", "authority"]) put(`signer.${k}`, prev.signer?.[k], "ваші попередні документи");
  }
  // 2. Реєстри ДПС: повна назва, адреса з реєстру обліку, ІПН і статус ПДВ
  if (v) {
    put("name", v.name, dpsLabel);
    put("address", v.registration?.address, dpsLabel);
    if (v.vat?.state === "registered" || (v.vat?.vatNumber && !["annulled", "not_registered"].includes(v.vat?.state))) put("ipn", v.vat.vatNumber, dpsLabel);
    put("vatStatus", v.vat?.state === "not_registered" ? "не платник ПДВ" : v.vat?.state === "annulled" ? "реєстрацію платника ПДВ анульовано" : v.vat?.vatNumber ? "платник ПДВ" : "", dpsLabel);
    if (v.registration?.closedAt) warnings.push(`Знято з обліку в ДПС ${v.registration.closedAt.split("-").reverse().join(".")}`);
    if (v.vat?.state === "annulled") warnings.push("Реєстрацію платника ПДВ анульовано — ПН такому покупцю не складається як платнику");
  }
  // 3. ЄДР: повна назва й керівник (підписант договору); стан юрособи
  if (edr) {
    const edrLabel = `ЄДР${edrAsOf ? ` на ${String(edrAsOf).split("-").reverse().join(".")}` : ""}`;
    put("name", edr.name, edrLabel);
    const s = edrSigner(edr.signers);
    if (s && !fields.signer.name) { put("signer.name", s.name, edrLabel); put("signer.position", s.position, edrLabel); }
    if (edr.stan && !/^зареєстровано$/i.test(edr.stan.trim())) warnings.push(`Стан у ЄДР: ${edr.stan}`);
  }
  // 4. ЄРПН: назва й ІПН з ПН/РК
  if (erpn) { put("name", erpn.name, "ПН/РК"); put("ipn", erpn.ipn, "ПН/РК"); }
  if (fields.signer.name && !fields.signer.authority && /^\d{8}$/.test(tin)) { fields.signer.authority = "Статуту"; sources["signer.authority"] = "типово для юрособи"; }
  const missing = [];
  if (!fields.address) missing.push("адреса");
  if (/^\d{8}$/.test(tin) && !fields.signer.name) missing.push("підписант");
  if (!fields.iban) missing.push("IBAN");
  return { fields, sources, warnings, missing, dpsCheckedAt: dps?.checkedAt || null };
}

// Доповнити наявні реквізити (порожні поля) з автоматичних; введене вами не перезаписуємо
export function fillCounterparty(current = {}, auto = {}) {
  const out = { ...auto, ...Object.fromEntries(Object.entries(current).filter(([k, x]) => k !== "signer" && String(x ?? "").trim())) };
  // Банк — лише від того самого IBAN: свій IBAN без банку не отримує банк іншого рахунку
  if (String(current.iban || "").trim() && current.iban !== auto.iban && !String(current.bank || "").trim()) out.bank = "";
  out.signer = { ...(auto.signer || {}), ...Object.fromEntries(Object.entries(current.signer || {}).filter(([, x]) => String(x ?? "").trim())) };
  return out;
}
